/**
 * Speaker Dictionary Utility
 * Manages a custom dictionary of words that users can add and use for word replacement
 * Persists to localStorage
 */

const STORAGE_KEY = 'speaker_dictionary';

/**
 * Get all words from the dictionary
 * @returns {string[]} Array of dictionary words
 */
export const getDictionaryWords = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const words = JSON.parse(stored);
      return Array.isArray(words) ? words : [];
    }
    return [];
  } catch (error) {
    console.error('Error reading speaker dictionary:', error);
    return [];
  }
};

/**
 * Add a word to the dictionary
 * @param {string} word - Word to add
 * @returns {boolean} True if word was added, false if it already exists
 */
export const addWordToDictionary = (word) => {
  if (!word || typeof word !== 'string') return false;

  const trimmedWord = word.trim();
  if (!trimmedWord) return false;

  try {
    const words = getDictionaryWords();

    // Check if word already exists (case-insensitive)
    const exists = words.some(w => w.toLowerCase() === trimmedWord.toLowerCase());
    if (exists) return false;

    words.push(trimmedWord);
    // Sort alphabetically for easier browsing
    words.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

    localStorage.setItem(STORAGE_KEY, JSON.stringify(words));
    return true;
  } catch (error) {
    console.error('Error adding word to dictionary:', error);
    return false;
  }
};

/**
 * Remove a word from the dictionary
 * @param {string} word - Word to remove
 * @returns {boolean} True if word was removed
 */
export const removeWordFromDictionary = (word) => {
  if (!word || typeof word !== 'string') return false;

  const trimmedWord = word.trim().toLowerCase();
  if (!trimmedWord) return false;

  try {
    const words = getDictionaryWords();
    const filteredWords = words.filter(w => w.toLowerCase() !== trimmedWord);

    if (filteredWords.length === words.length) return false;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredWords));
    return true;
  } catch (error) {
    console.error('Error removing word from dictionary:', error);
    return false;
  }
};

/**
 * Check if a word exists in the dictionary
 * @param {string} word - Word to check
 * @returns {boolean} True if word exists
 */
export const isWordInDictionary = (word) => {
  if (!word || typeof word !== 'string') return false;

  const trimmedWord = word.trim().toLowerCase();
  if (!trimmedWord) return false;

  const words = getDictionaryWords();
  return words.some(w => w.toLowerCase() === trimmedWord);
};

/**
 * Search dictionary for words matching a query
 * @param {string} query - Search query
 * @returns {string[]} Matching words
 */
export const searchDictionary = (query) => {
  if (!query || typeof query !== 'string') return getDictionaryWords();

  const lowerQuery = query.trim().toLowerCase();
  if (!lowerQuery) return getDictionaryWords();

  const words = getDictionaryWords();
  return words.filter(w => w.toLowerCase().includes(lowerQuery));
};

/**
 * Clear all words from the dictionary
 */
export const clearDictionary = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing dictionary:', error);
  }
};

/**
 * Export dictionary as JSON string
 * @returns {string} JSON string of dictionary words
 */
export const exportDictionary = () => {
  const words = getDictionaryWords();
  return JSON.stringify(words, null, 2);
};

/**
 * Import dictionary from JSON string (merges with existing)
 * @param {string} jsonString - JSON string of words array
 * @returns {number} Number of new words added
 */
export const importDictionary = (jsonString) => {
  try {
    const importedWords = JSON.parse(jsonString);
    if (!Array.isArray(importedWords)) {
      throw new Error('Invalid dictionary format');
    }

    let addedCount = 0;
    importedWords.forEach(word => {
      if (typeof word === 'string' && addWordToDictionary(word)) {
        addedCount++;
      }
    });

    return addedCount;
  } catch (error) {
    console.error('Error importing dictionary:', error);
    return 0;
  }
};

export default {
  getDictionaryWords,
  addWordToDictionary,
  removeWordFromDictionary,
  isWordInDictionary,
  searchDictionary,
  clearDictionary,
  exportDictionary,
  importDictionary
};
